import { FiScissors } from 'react-icons/fi';

export const prefixesSuffixesData = {
  courseId: 'prefixes-suffixes',
  courseType: 'vocabulary',
  title: 'Prefixes & Suffixes',
  icon: FiScissors,
  overview: {
    impact: 'Decode unfamiliar words by recognising common word parts — a vital strategy for IELTS Reading',
    focus: 'Common prefixes (un-, re-, dis-, mis-, pre-, over-, under-), suffixes (-able, -ful, -less, -ous, -ive), and spelling rules',
    uses: 'Reading comprehension, vocabulary expansion, Writing accuracy',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'Common Prefixes',
      content: `<p>A <strong>prefix</strong> is added to the <em>beginning</em> of a word to change its meaning. Learning prefixes lets you guess the meaning of unknown words in IELTS Reading passages.</p>
<table>
  <thead><tr><th>Prefix</th><th>Meaning</th><th>Examples</th></tr></thead>
  <tbody>
    <tr><td>un-</td><td>not, opposite</td><td>unhappy, unusual, unable, uncertain</td></tr>
    <tr><td>re-</td><td>again, back</td><td>rewrite, rebuild, reconsider, review</td></tr>
    <tr><td>dis-</td><td>not, opposite, remove</td><td>disagree, disappear, disconnect, dislike</td></tr>
    <tr><td>mis-</td><td>wrongly, badly</td><td>misunderstand, mislead, misspell, misuse</td></tr>
    <tr><td>pre-</td><td>before</td><td>predict, prevent, preview, prehistoric</td></tr>
    <tr><td>over-</td><td>too much, above</td><td>overwork, overcrowded, overestimate, overlook</td></tr>
    <tr><td>under-</td><td>too little, below</td><td>underestimate, underpaid, undergraduate, underline</td></tr>
  </tbody>
</table>`,
      points: [
        'Negative prefixes (un-, dis-, mis-, in-/im-/il-/ir-) are the most common in IELTS',
        'The prefix does not change the word class: happy (adj) → unhappy (adj)',
        'Some prefixes have overlapping meanings — context determines which to use'
      ]
    },
    {
      title: 'Common Suffixes',
      content: `<p>A <strong>suffix</strong> is added to the <em>end</em> of a word. Unlike prefixes, suffixes usually change the word class (e.g., verb → noun).</p>
<table>
  <thead><tr><th>Suffix</th><th>Word class formed</th><th>Examples</th></tr></thead>
  <tbody>
    <tr><td>-able / -ible</td><td>Adjective (can be done)</td><td>comfortable, flexible, affordable, accessible</td></tr>
    <tr><td>-ful</td><td>Adjective (full of)</td><td>helpful, powerful, beautiful, meaningful</td></tr>
    <tr><td>-less</td><td>Adjective (without)</td><td>homeless, careless, endless, meaningless</td></tr>
    <tr><td>-ous / -ious</td><td>Adjective</td><td>dangerous, various, previous, ambitious</td></tr>
    <tr><td>-ive</td><td>Adjective</td><td>creative, effective, competitive, sensitive</td></tr>
    <tr><td>-tion / -sion</td><td>Noun</td><td>education, decision, pollution, permission</td></tr>
    <tr><td>-ment</td><td>Noun</td><td>development, improvement, environment, government</td></tr>
    <tr><td>-ness</td><td>Noun</td><td>happiness, awareness, kindness, weakness</td></tr>
    <tr><td>-ly</td><td>Adverb</td><td>quickly, carefully, significantly, recently</td></tr>
  </tbody>
</table>`,
      points: [
        'The pair -ful (with) and -less (without) are opposites: hopeful ↔ hopeless',
        'Suffixes often signal the word class needed for a gap-fill in IELTS Reading',
        'Some suffixes are interchangeable only with specific roots — always check a dictionary when unsure'
      ]
    },
    {
      title: 'Spelling Rules When Adding Affixes',
      content: `<p>Adding prefixes and suffixes can trigger spelling changes. Knowing these rules avoids costly errors in IELTS Writing.</p>
<ul>
  <li><strong>Drop silent "e" before a vowel suffix:</strong> create → creat<em>ive</em>, use → us<em>able</em></li>
  <li><strong>Keep "e" before a consonant suffix:</strong> care → care<em>ful</em>, hope → hope<em>less</em></li>
  <li><strong>Change "y" to "i":</strong> happy → happ<em>iness</em>, beauty → beaut<em>iful</em> (but: play → play<em>ful</em> — "y" after a vowel stays)</li>
  <li><strong>Double the final consonant (CVC rule):</strong> big → big<em>ger</em>, stop → stop<em>ping</em></li>
  <li><strong>Prefixes rarely change spelling:</strong> un + necessary = unnecessary (double "n")</li>
</ul>`,
      points: [
        'The "drop e" rule has exceptions: noticeable, changeable (keep "e" to preserve the soft "c" / "g")',
        'Doubling only applies to short stressed syllables: "begin" → "beginning" but "open" → "opening"',
        'Misspelling a word with a prefix or suffix in IELTS Writing lowers your Lexical Resource score'
      ]
    },
    {
      title: 'Prefix + Suffix Combinations',
      content: `<p>Many academic words use <strong>both</strong> a prefix and a suffix around a root. Recognising all three parts unlocks meaning instantly.</p>
<table>
  <thead><tr><th>Word</th><th>Prefix</th><th>Root</th><th>Suffix</th><th>Meaning</th></tr></thead>
  <tbody>
    <tr><td>uncomfortable</td><td>un- (not)</td><td>comfort</td><td>-able (can be)</td><td>not able to be comfortable</td></tr>
    <tr><td>disagreement</td><td>dis- (not)</td><td>agree</td><td>-ment (noun)</td><td>a state of not agreeing</td></tr>
    <tr><td>irreplaceable</td><td>ir- (not)</td><td>replace</td><td>-able (can be)</td><td>cannot be replaced</td></tr>
    <tr><td>overproduction</td><td>over- (too much)</td><td>produce</td><td>-tion (noun)</td><td>producing too much</td></tr>
    <tr><td>misunderstanding</td><td>mis- (wrongly)</td><td>understand</td><td>-ing (noun/adj)</td><td>wrong understanding</td></tr>
  </tbody>
</table>`,
      points: [
        'Breaking a complex word into prefix + root + suffix is the fastest way to decode it in a reading exam',
        'Many IELTS Reading answers require you to form the correct prefix-suffix combination',
        'Practice: take a root word and build as many prefix + suffix combos as possible'
      ]
    }
  ],

  exercises: [
    {
      title: 'Match Prefix to Meaning',
      type: 'select',
      instructions: 'Choose the correct meaning for each prefix.',
      questions: [
        {
          text: 'What does the prefix "mis-" mean?',
          options: ['again', 'wrongly or badly', 'not or opposite', 'before'],
          answer: 'wrongly or badly',
          explanation: '"Mis-" means wrongly or badly, as in "misunderstand" (understand wrongly) or "mislead" (lead in the wrong direction).'
        },
        {
          text: 'What does the prefix "pre-" mean?',
          options: ['after', 'too much', 'before', 'not'],
          answer: 'before',
          explanation: '"Pre-" means before, as in "preview" (view before), "predict" (say before it happens).'
        },
        {
          text: 'What does the prefix "over-" mean?',
          options: ['under', 'too much or above', 'again', 'wrongly'],
          answer: 'too much or above',
          explanation: '"Over-" means too much or above, as in "overwork" (work too much), "overcrowded" (too crowded).'
        },
        {
          text: 'What does the prefix "under-" mean?',
          options: ['too much', 'not', 'too little or below', 'again'],
          answer: 'too little or below',
          explanation: '"Under-" means too little or below, as in "underpaid" (paid too little), "underestimate" (estimate too low).'
        }
      ]
    },
    {
      title: 'Add the Correct Prefix',
      type: 'fill-in',
      instructions: 'Add the correct prefix to complete each word. Write the full word.',
      questions: [
        {
          text: 'Many students _____estimate the time needed for exam preparation. (= estimate too little)',
          answer: 'underestimate',
          explanation: '"Under-" means too little, so "underestimate" means to estimate something as less than it really is.'
        },
        {
          text: 'Please _____read the instructions carefully before the test. (= read again)',
          answer: 'reread',
          explanation: '"Re-" means again, so "reread" means to read something again.'
        },
        {
          text: 'The data was _____interpreted, leading to wrong conclusions. (= interpreted wrongly)',
          answer: 'misinterpreted',
          explanation: '"Mis-" means wrongly, so "misinterpreted" means interpreted incorrectly.'
        },
        {
          text: 'It is _____possible to finish this task in one hour. (= not possible)',
          answer: 'impossible',
          explanation: '"Im-" is used before words starting with "m" or "p" to mean "not". "Impossible" means not possible.'
        }
      ]
    },
    {
      title: 'Add the Correct Suffix',
      type: 'fill-in',
      instructions: 'Add the correct suffix to the root word in brackets. Write the full word.',
      questions: [
        {
          text: 'The new policy was extremely _______ in reducing pollution. (effect + suffix → adjective)',
          answer: 'effective',
          explanation: 'Adding "-ive" to "effect" creates the adjective "effective", meaning successful in producing a result.'
        },
        {
          text: 'Her _______ to public speaking made the audience uneasy. (nervous + suffix → noun)',
          answer: 'nervousness',
          explanation: 'Adding "-ness" to "nervous" creates the noun "nervousness", meaning the state of being nervous.'
        },
        {
          text: 'This information is freely _______ on the internet. (access + suffix → adjective)',
          answer: 'accessible',
          explanation: 'Adding "-ible" to "access" creates the adjective "accessible", meaning able to be accessed.'
        },
        {
          text: 'The _______ of the new hospital took three years. (construct + suffix → noun)',
          answer: 'construction',
          explanation: 'Adding "-tion" to "construct" creates the noun "construction", meaning the process of building.'
        }
      ]
    },
    {
      title: 'Identify the Meaning Change',
      type: 'select',
      instructions: 'Choose the correct meaning of each word based on its prefix or suffix.',
      questions: [
        {
          text: 'What does "irreversible" mean?',
          options: [
            'can be reversed again',
            'cannot be reversed',
            'was reversed before',
            'partly reversed'
          ],
          answer: 'cannot be reversed',
          explanation: '"Ir-" means "not" and "-ible" means "able to be". So "irreversible" means "not able to be reversed".'
        },
        {
          text: 'What does "overqualified" mean?',
          options: [
            'not qualified enough',
            'qualified again',
            'having more qualifications than needed',
            'qualified before'
          ],
          answer: 'having more qualifications than needed',
          explanation: '"Over-" means "too much", so "overqualified" means having more qualifications than a position requires.'
        },
        {
          text: 'What does "meaningless" mean?',
          options: [
            'full of meaning',
            'having some meaning',
            'without meaning',
            'meaning again'
          ],
          answer: 'without meaning',
          explanation: 'The suffix "-less" means "without". "Meaningless" means without meaning or purpose.'
        }
      ]
    },
    {
      title: 'Create Words with Affixes',
      type: 'writing',
      instructions: 'Use prefixes and/or suffixes to form words as described, then write a sentence using each word.',
      questions: [
        {
          text: 'Form a word meaning "not able to be replaced" and use it in an IELTS-style sentence.',
          example: 'Irreplaceable cultural artefacts were damaged in the fire, highlighting the need for better museum preservation systems.',
          tip: 'Combine the prefix "ir-" (not) + root "replace" + suffix "-able" (able to be) = irreplaceable.'
        },
        {
          text: 'Form a word meaning "the state of being unemployed" and use it in an IELTS-style sentence.',
          example: 'Youth unemployment has risen sharply in recent years, prompting governments to invest in vocational training programmes.',
          tip: 'Combine prefix "un-" (not) + root "employ" + suffix "-ment" (noun) = unemployment.'
        }
      ]
    }
  ],

  tips: [
    'When you meet an unknown word in IELTS Reading, break it into prefix + root + suffix to guess its meaning',
    'Learn prefixes and suffixes in pairs of opposites: -ful/-less, over-/under-, pre-/post-',
    'Pay attention to spelling changes when adding suffixes — misspellings lower your Writing score',
    'Keep a list of words with the same prefix (e.g., all "dis-" words) to strengthen pattern recognition'
  ],
  dailyPractice: [
    'Pick one prefix and one suffix each day — find 5 words using each',
    'Read a short English article and circle all prefixed/suffixed words you can identify',
    'Practice the "build-a-word" game: take a root and add as many affixes as possible',
    'Write 3 IELTS-style sentences each day using words with prefixes and suffixes'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Prefixes & Suffixes: Common prefixes (un-, re-, dis-, mis-, pre-, over-, under-) and suffixes (-able, -ful, -less, -ous, -ive, -tion, -ment, -ness). Covers how affixes change meaning and word class, spelling rules, and prefix-suffix combinations. Difficulty 2 (pre-intermediate). Exercises include prefix matching, gap-fill with prefixes/suffixes, meaning identification, and word creation with sentences.'
};

export function getDataForDifficulty(difficulty = 2) {
  if (difficulty <= 2) {
    return {
      ...prefixesSuffixesData,
      title: prefixesSuffixesData.title + ' (Foundations)',
      overview: { ...prefixesSuffixesData.overview, duration: '15-20 minutes' },
      sections: prefixesSuffixesData.sections.slice(0, Math.max(2, Math.ceil(prefixesSuffixesData.sections.length / 2))),
      exercises: prefixesSuffixesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...prefixesSuffixesData,
      title: prefixesSuffixesData.title + ' (Advanced)',
      overview: { ...prefixesSuffixesData.overview, duration: '30-35 minutes' },
      sections: prefixesSuffixesData.sections,
      exercises: prefixesSuffixesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(prefixesSuffixesData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return prefixesSuffixesData;
}
